import type { AuthenticatedUser } from '@testflow/shared';
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { request } from './api.js';

// GET /v1/auth/me liefert entweder den vollständigen AuthenticatedUser oder — solange die
// Session-Kopplung fehlt (siehe apps/api/src/routes/auth.ts, Task 8b) — den Stub
// `{ authenticated: false }`. Beide Formen werden hier sauber auf `user: null` abgebildet.
type AuthMeResponse = { authenticated: false } | AuthenticatedUser;

function isAuthenticatedUser(value: AuthMeResponse): value is AuthenticatedUser {
  return 'user_id' in value;
}

type SessionContextValue = {
  user: AuthenticatedUser | null;
  loading: boolean;
};

const SessionContext = createContext<SessionContextValue>({ user: null, loading: true });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    request<AuthMeResponse>('/v1/auth/me')
      .then((me) => {
        if (!cancelled) setUser(isAuthenticatedUser(me) ? me : null);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // nur einmal beim Mount
  }, []);

  return <SessionContext.Provider value={{ user, loading }}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  return useContext(SessionContext);
}
